import { createSelector } from 'reselect';
import { createCachedSelector } from 're-reselect';
import { initialState } from './initialState';
import { get, min, max, map, isEmpty, find, orderBy } from 'lodash';
import * as d3 from 'd3';
import * as constants from './constants';
import { serializeTableData } from 'ui-component/tables/serialization';
import { getDirectionalSurveyDataForSelectedUnit } from 'utils/directional_survey/main2';
import { P_TABLE_COLUMNS } from 'pages/drilling-completion/PluggingDesign/schema/table/pressure';
// Pump
import { SCHEMA_TIME_SERIES } from 'pages/drilling-completion/PluggingDesign/schema/table/output_time_series';
// Summary
import { SCHEMA_DETAIL } from 'pages/drilling-completion/PluggingDesign/schema/table/output_detail';
// Key Info
import { SCHEMA_SUMMARY } from 'pages/drilling-completion/PluggingDesign/schema/table/output_summary';
// Plug Program
import { SCHEMA_RECOMMEND } from 'pages/drilling-completion/PluggingDesign/schema/table/output_plug_program';

const getSchemaWithUom = (schema, uom) => {
  const newSchema = schema.map((col) => {
    const colId = get(col, 'col_id', null);
    const newCol = { ...col };
    if (!isEmpty(colId)) {
      newCol.col_unit = get(uom, `${colId}_uom`, '-');
    }
    return newCol;
  });
  return newSchema;
};

// CURRENT STORE SELECTORS
// ==================================================================================================

export const getStore = (store) => store[constants.NAMESPACE] || initialState;
export const currentLayoutSelector = (store) => getStore(store).currentLayout;

// Project
export const projectIdsSelector = (store) => getStore(store).projectIds;
export const projectsByIdSelector = (store) => getStore(store).projectsById;
export const selectedProjectIdsSelector = (store) => getStore(store).selectedProjectIds;

// Selected Items
export const selectedZoneIdsSelector = (store) => getStore(store).selectedZoneIds;
export const selectedCasingIdsSelector = (store) => getStore(store).selectedCasingIds;
export const selectedPlugIdsSelector = (store) => getStore(store).selectedPlugIds;

// Project Input Data
export const inputGeneralByProjectIdSelector = (store) => getStore(store).inputGeneralByProjectId;
export const inputPressureByProjectIdSelector = (store) => getStore(store).inputPressureByProjectId;
export const inputPlugProgramByProjectIdSelector = (store) => getStore(store).inputPlugProgramByProjectId;
export const inputCasingProgramByProjectIdSelector = (store) => getStore(store).inputCasingProgramByProjectId;
export const inputZoneProgramByProjectIdSelector = (store) => getStore(store).inputZoneProgramByProjectId;

// Project Output Data
export const outputSummaryByProjectIdSelector = (store) => getStore(store).outputSummaryByProjectId;
export const outputDetailByProjectIdSelector = (store) => getStore(store).outputDetailByProjectId;
export const outputTimeSeriesByProjectIdSelector = (store) => getStore(store).outputTimeSeriesByProjectId;
export const outputPlugProgramByProjectIdSelector = (store) => getStore(store).outputPlugProgramByProjectId;
export const outputSpaceByProjectIdSelector = (store) => getStore(store).outputSpaceByProjectId;

export const settingsSelector = (store) => getStore(store).settings;
export const projectsSelector = createSelector(projectIdsSelector, projectsByIdSelector, (ids, byId) => ids.map((id) => byId[id]));

// CURRENT PROJECT SELECTORS
// ==================================================================================================

export const currentProjectIdSelector = createSelector(selectedProjectIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});
export const currentCasingIdSelector = createSelector(selectedCasingIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

export const currentPlugIdSelector = createSelector(selectedPlugIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

export const currentZoneIdSelector = createSelector(selectedZoneIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

// Note: this will limit how many case data will be rendered
export const renderProjectIdsSelector = createSelector(selectedProjectIdsSelector, (selectedProjectIds) => selectedProjectIds.slice(0, 10));
export const currentProjectSelector = createSelector(projectsByIdSelector, currentProjectIdSelector, (byId, id) => byId[id] || {});
export const currentGeneralSelector = createSelector(
  inputGeneralByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentPressureSelector = createSelector(
  inputPressureByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentInputPlugProgramSelector = createSelector(
  inputPlugProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentInputZoneProgramSelector = createSelector(
  inputZoneProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentInputCasingProgramSelector = createSelector(
  inputCasingProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentProjectOutputSummarySelector = createSelector(
  outputSummaryByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentProjectOuputDetailSelector = createSelector(
  outputDetailByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentProjectOutputTimeSeriesSelector = createSelector(
  outputTimeSeriesByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentProjectOuputSpaceSelector = createSelector(
  outputSpaceByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentProjectOutputPlugProgramSelector = createSelector(
  outputPlugProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

// UI Dropdown
// ==================================================================================================

export const currentCasingOptionSelector = createSelector(currentInputCasingProgramSelector, (obj) => {
  const casingPrograms = get(obj, 'casing_program', []);
  const casingOption = [
    {
      id: 'None',
      label: 'No selection'
    }
  ];
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    const casingType = get(casingProgram, 'casing_type', 'UNKNOWN');
    const setDepth = get(casingProgram, 'set_depth', '');
    const setDepthUom = get(casingProgram, 'set_depth_uom', '');
    const label = `${idx + 1} - ${casingType} ${setDepth} ${setDepthUom}`;
    casingOption.push({
      id: casingId,
      label
    });
  });
  return casingOption;
});

export const currentCasingIdsSelector = createSelector(currentInputCasingProgramSelector, (obj) => {
  const casingPrograms = get(obj, 'casing_program', []);
  const casingIds = [];
  casingPrograms.forEach((casingProgram) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    casingIds.push(casingId);
  });
  return casingIds;
});

export const currentPlugTableRowsSelector = createSelector(currentInputPlugProgramSelector, (obj) => {
  const plugs = get(obj, 'plug_program', []);
  const tableRows = [];
  plugs.forEach((item) => {
    const row = {
      id: item.id,
      name: get(item, 'name', ''),
      design_type: get(item, 'design_type', ''),
      plug_bot_depth: get(item, 'plug_bot_depth', null),
      plug_bot_depth_uom: get(item, 'plug_bot_depth_uom', null)
    };

    tableRows.push(row);
  });
  // Sorted by set depth
  const sortedTableRows = orderBy(tableRows, ['plug_bot_depth'], ['asc']);
  const newTableRows = sortedTableRows.map((item, index) => {
    const obj = { ...item, index: index + 1 };
    return obj;
  });

  return newTableRows;
});

export const currentZoneTableRowsSelector = createSelector(currentInputZoneProgramSelector, (obj) => {
  const zones = get(obj, 'zone_program', []);
  const tableRows = [];
  zones.forEach((item) => {
    tableRows.push(item);
  });
  // Sorted by set depth
  const sortedTableRows = orderBy(tableRows, ['top_depth'], ['asc']);
  const newTableRows = sortedTableRows.map((item, index) => {
    const obj = { ...item, index: index + 1 };
    return obj;
  });

  return newTableRows;
});

export const currentPlugOptionsSelector = createSelector(currentPlugTableRowsSelector, (plugRows) => {
  const plugOption = [
    {
      id: 'None',
      label: 'No selection'
    }
  ];
  plugRows.forEach((row, idx) => {
    const plugId = get(row, 'id', 'UNKNOWN');
    // const casingType = get(row, 'casing_type', 'UNKNOWN');
    const plugName = get(row, 'name', '');
    const stage = get(row, 'stage', 1);
    // const designedDepth = get(row, 'designed_depth', '');
    // const designedDepthUom = get(row, 'designed_depth_uom', '');
    const label = `${idx + 1} - ${plugName}`;
    plugOption.push({
      id: plugId,
      label,
      name: plugName,
      stage
    });
  });
  return plugOption;
});

// CURRENT OUTPUT SUMMARY
// ==================================================================================================

export const currentOutputSummaryUomSelector = createSelector(currentProjectOutputSummarySelector, (obj) => get(obj, 'summary_uom', {}));

export const currentOutputSummarySelector = createSelector(currentProjectOutputSummarySelector, (obj) => {
  const uom = get(obj, 'summary_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SUMMARY, uom);

  const df = {
    columns: get(obj, 'summary_cols', []),
    data: get(obj, 'summary_data_uom', []),
    schema
  };

  return serializeTableData(df);
});

// CURRENT OUTPUT DETAIL
// ==================================================================================================

export const currentOutputDetailUomSelector = createSelector(currentProjectOuputDetailSelector, (obj) => get(obj, 'detail_uom', {}));

export const currentOutputDetailSelector = createSelector(currentProjectOuputDetailSelector, (obj) => {
  const uom = get(obj, 'detail_uom', {});
  const schema = getSchemaWithUom(SCHEMA_DETAIL, uom);
  const df = {
    columns: get(obj, 'detail_cols', []),
    data: get(obj, 'detail_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// CURRENT OUTPUT TIME SERIES
// ==================================================================================================

export const currentOutputTimeSeriesUomSelector = createSelector(currentProjectOutputTimeSeriesSelector, (obj) =>
  get(obj, 'time_series_uom', {})
);

export const currentOutputTimeSeriesSelector = createSelector(currentProjectOutputTimeSeriesSelector, (obj) => {
  const uom = get(obj, 'time_series_uom', {});
  const schema = getSchemaWithUom(SCHEMA_TIME_SERIES, uom);
  const df = {
    columns: get(obj, 'time_series_cols', []),
    data: get(obj, 'time_series_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// CURRENT OUTPUT PLUG PROGRAM
// ==================================================================================================

export const currentOutputPlugProgramUomSelector = createSelector(currentProjectOutputPlugProgramSelector, (obj) =>
  get(obj, 'recommend_tbl_uom', {})
);

export const currentOutputPlugProgramSelector = createSelector(currentProjectOutputPlugProgramSelector, (obj) => {
  const uom = get(obj, 'recommend_tbl_uom', {});
  const schema = getSchemaWithUom(SCHEMA_RECOMMEND, uom);
  const df = {
    columns: get(obj, 'recommend_tbl_cols', []),
    data: get(obj, 'recommend_tbl_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// ADDITIONAL SELECTORS FOR CHARTS

export const surveyDataSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'p_tbl', []));

export const currentMethodSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'directional_survey_method', undefined));

export const colorAttrSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_attr', null));

export const colorStopsSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_stops', null));

export const colorAttrMinSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMin = min(rowData.map((d) => d[colorAttr]));
  return attrMin;
});

export const colorAttrMaxSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMax = max(rowData.map((d) => d[colorAttr]));
  return attrMax;
});

// COMPARE CASES SELECTOR
// ==========================

export const currentSurveyTblSelector = createSelector(currentPressureSelector, (obj) => {
  const data = getDirectionalSurveyDataForSelectedUnit(obj);
  const df = {
    columns: P_TABLE_COLUMNS.map((col) => col.col_id),
    data,
    schema: P_TABLE_COLUMNS
  };
  return serializeTableData(df);
});

export const currentCasingObjectByIdSelector = createSelector(currentInputCasingProgramSelector, (obj) => {
  const byId = {};
  const casingPrograms = get(obj, 'casing_program', []);
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    byId[casingId] = casingProgram;
  });
  return byId;
});

export const dataByProjectIdSelector = createCachedSelector(
  projectsByIdSelector,
  inputPressureByProjectIdSelector,
  (state, projectId) => projectId,
  (projectsById, inputPressureByProjectId, projectId) => {
    // Survey Data
    const rowData = get(inputPressureByProjectId, `${projectId}.p_tbl`, []);
    const uom = get(inputPressureByProjectId, `${projectId}.p_tbl_uom`, []);
    const colorAttr = get(projectsById, `${projectId}.survey_chart.color_attr`, 'z');
    const colorStops = get(projectsById, `${projectId}.survey_chart.color_stops`, null);
    const name = get(projectsById, `${projectId}.name`, null);

    // Chart Data
    const colorAttrData = rowData.map((d) => d[colorAttr]);
    const x = rowData.map((d) => d.x);
    const y = rowData.map((d) => d.y);
    const z = rowData.map((d) => d.z);
    const minColorAttr = min(colorAttrData);
    const maxColorAttr = max(colorAttrData);
    const colorData = [];

    if (!isEmpty(colorStops)) {
      const offsets = map(colorStops, 'offset');
      const colors = map(colorStops, 'color');
      const colorScale = d3.scale.linear().domain(offsets).range(colors).interpolate(d3.interpolateRgb);
      colorAttrData.forEach((value) => {
        let color = '#0000ff';
        if (maxColorAttr > minColorAttr && colorScale) {
          const offset = (value - minColorAttr) / (maxColorAttr - minColorAttr);
          color = colorScale(offset);
        }
        colorData.push(color);
      });
    }

    return {
      rowData,
      x,
      y,
      z,
      maxColorAttr,
      minColorAttr,
      name,
      colorData,
      uom
    };
  }
)(
  (state, projectId) => projectId // Cache selectors by projectId
);
